import React from 'react';

import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { NoteField } from '../../../NoteField';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import { FormFieldset } from '../../../shared/FormFieldset';
import { SelectList } from '../../../shared/SelectList';
import AccountField from '../../AccountField';
import FieldSkeleton from '../../FieldSkeleton';
import ReceiverField from '../../Payment/ReceiverField';
import { SelectedReceiver } from '../../SelectedReceiver';
import AmountLimitField from '../AmountLimitField';
import KidField from '../KidField';
import { AvtaleGiroActionType } from '../form/actions';
import useFormContext from '../form/useFormContext';
import { MAX_NOTE_LIMIT } from '../form/validate';
import { FieldProps } from './FieldProps';

const AvtalegiroMobileFields = ({
    locked,
    receiverLock,
    kidLock,
    accounts,
    invalidAccounts,
    accountState,
    freeWithdrawals,
    animateModalAs,
    emptyFieldWarningLabel,
}: FieldProps) => {
    const { state, handleChange, errors, curryChange } = useFormContext();
    const { targetFromAccount } = usePaymentAccountContext();
    const translated = useTranslation();
    return (
        <FormFieldset disabled={locked}>
            <SelectList>
                <li>
                    {accountState.isLoading ? (
                        <FieldSkeleton />
                    ) : (
                        <AccountField
                            id="avtalegiroFromAccount"
                            locked={locked}
                            placeholder={translated.payFromAccount}
                            buttonLabel={translated.from}
                            title={translated.paymentFrom}
                            accountNumber={state.fromAccount}
                            account={targetFromAccount}
                            accounts={accounts ?? []}
                            invalidAccounts={invalidAccounts}
                            error={errors.accountId}
                            onChange={(accountNumber) => {
                                handleChange({
                                    type: AvtaleGiroActionType.FROM_ACCOUNT_SELECTED,
                                    payload: accountNumber,
                                });
                            }}
                            isLoading={accountState.isLoading}
                            disabled={locked}
                            refresh={accountState.refetch}
                            showModal={state.showFromAccounts}
                            setShowModal={(showModal: boolean) => {
                                handleChange({
                                    type: showModal
                                        ? AvtaleGiroActionType.SHOW_FROM_ACCOUNTS
                                        : AvtaleGiroActionType.HIDE_FROM_ACCOUNTS,
                                });
                            }}
                            bottomLabel={
                                freeWithdrawals.show ? (
                                    <AccountFreeWithdrawalLabel freeWithdrawals={freeWithdrawals.data} />
                                ) : undefined
                            }
                            animateModalAs={animateModalAs}
                            customLabel={emptyFieldWarningLabel}
                        />
                    )}
                </li>
                <li>
                    <ReceiverField
                        receiver={state.toAccount}
                        locked={locked || receiverLock}
                        onChange={(receiver: SelectedReceiver) =>
                            handleChange({ type: AvtaleGiroActionType.TO_ACCOUNT_SELECTED, payload: receiver })
                        }
                        error={errors.toAccount}
                        animateModalAs={animateModalAs}
                    />
                </li>
                <li>
                    <KidField
                        toAccount={state.toAccount}
                        kid={state.kid}
                        locked={locked || kidLock}
                        labelText={translated.avtaleGiroAgreement.kidField}
                        placeholder={translated.avtaleGiroAgreement.kidPlaceholder}
                        error={errors.kid}
                        onChange={(value) =>
                            value && handleChange({ type: AvtaleGiroActionType.SET_KID, payload: value })
                        }
                        animateModalAs={animateModalAs}
                    />
                </li>
                <li>
                    <NoteField
                        note={state.note}
                        noteMaxLength={MAX_NOTE_LIMIT}
                        onChange={curryChange(AvtaleGiroActionType.NOTE)}
                        error={errors.note}
                        labelText={translated.avtaleGiroAgreement.ownText}
                        placeholder={translated.avtaleGiroAgreement.ownText}
                        locked={locked}
                        forceDisableKid
                    />
                </li>
                <AmountLimitField
                    transactionLimit={state.transactionLimit}
                    monthlyLimit={state.monthlyLimit}
                    locked={locked}
                    error={{ transaction: errors.transactionLimit, monthly: errors.monthlyLimit }}
                    onChange={curryChange(AvtaleGiroActionType.TRANSACTION_AMOUNT_LIMIT)}
                    onChangeMonthlyLimit={curryChange(AvtaleGiroActionType.MONTHLY_AMOUNT_LIMIT)}
                    animateModalAs={animateModalAs}
                />
            </SelectList>
        </FormFieldset>
    );
};

export default AvtalegiroMobileFields;
